import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import styles from "./PatientOverview.module.css";
import { SkeletonBarGroup } from "../Skeleton";

const MonthlyComplianceChart = ({
  data = [],
  title = "Monthly Compliance",
  isLoading = false,
}) => {
  /* ---------- GREEN LABEL ---------- */
  const renderCompliantLabel = ({ x, y, width, value }) => {
    // show label even when value is 0; only skip if null/undefined
    if (value == null) return null;

    const centerX = x + width / 2;
    const labelY = y - 6; // position above bar

    return (
      <text
        x={centerX}
        y={labelY}
        textAnchor="middle"
        fill="#1b254b"
        fontSize={11}
        fontWeight={600}
      >
        {value}
      </text>
    );
  };

  /* ---------- RED LABEL ---------- */
  const renderNonCompliantLabel = ({ x, y, width, value }) => {
    if (value == null) return null;

    const centerX = x + width / 2;
    const labelY = y - 6;

    return (
      <text
        x={centerX}
        y={labelY}
        textAnchor="middle"
        fill="#3b82f6"
        fontSize={11}
        fontWeight={600}
      >
        {value}
      </text>
    );
  };

  // total labels removed; chart now only shows rpm_compliant and ccm_compliant bars.

  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};

    return (
      <div className={styles.tooltipBox}>
        <div className={styles.tooltipTitle}>{label}</div>

        <div className={styles.tooltipRow}>
          <span>RPM Compliant</span>
          <span>{row.rpm_compliant ?? 0}</span>
        </div>

        <div className={styles.tooltipRow}>
          <span>CCM Compliant</span>
          <span>{row.ccm_compliant ?? 0}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.chartCard}>
      <h3 className={styles.chartTitle}>{title}</h3>

      <div className={styles.chartBody}>
        {isLoading ? (
          <div className={styles.chartSkeleton}>
            <SkeletonBarGroup
              count={4}
              heights={["45%", "57%", "69%", "81%"]}
            />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={data}
              barSize={60}
              barGap={3}
              barCategoryGap="0%"
              margin={{ top: 30, right: 13, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f3f4f6"
              />

              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />

              <YAxis
                hide
                domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
              />

              <Tooltip
                cursor={{ fill: "transparent" }}
                content={renderTooltip}
              />

              <Bar
                dataKey="rpm_compliant"
                fill="#1b254b"
                radius={[2, 2, 0, 0]}
                // background={{ fill: '#f9fafb' }}
              >
                <LabelList content={renderCompliantLabel} />
              </Bar>

              <Bar
                dataKey="ccm_compliant"
                fill="#3b82f6"
                radius={[2, 2, 0, 0]}
                // background={{ fill: '#f9fafb' }}
              >
                <LabelList content={renderNonCompliantLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.compliantDot} />RPM Compliant
        </span>
        <span className={styles.legendItem}>
          <span className={styles.nonCompliantDot} />CCM Compliant
        </span>
      </div>
    </div>
  );
};

export default MonthlyComplianceChart;